import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import SPMapSection from "~/features/salesPartner/components/MapSection/SPMapSection";
import SearchCard, {
  type Option,
  type SASearchFormFields,
  SearchTypesSP,
} from "~/features/salesPartner/components/SearchCard/SalesPartnerSearchBox";
import SalesPartnersTilesList from "~/features/salesPartner/components/SalesPartnersTilesList/SalesPartnersTilesList";
import {
  type CenterValue,
  type CurrentLocation,
  type GoogleMarker,
  type SalesPartnerMountRequest,
} from "~/domains/salesPartner/types/SalesPartnerTypes";
import { redirectToUrl } from "~/utils/url/urlUtils";
import { fetchCurrentLocation } from "~/domains/salesPartner/api/fetchCurrentLocation";
import { type UrlParams } from "~/domains/salesPartner/api/getSalesPartnerSearch";
import { getBasicSalesPartnerData } from "~/domains/salesPartner/api/getBasicSalesPartnerData";
import { selectWishlistThunk } from "~/features/wishlist/redux/thunks";
import { useDispatch, useSelector } from "react-redux";
import { FormProvider, useForm } from "react-hook-form";
import { useIsMinDesktopSize, useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { GoogleApiProvider, useIsGoogleApiLoaded } from "~/features/salesPartner/providers/GoogleApiProvider";
import { Col, Row } from "~/common/components/Layout";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import Modal, { ModalSize } from "~/common/components/Modal";
import { SPCompanyDetails } from "~/features/salesPartner/components/CompanyDetailsCard/SPCompanyDetails";
import GmapsCardRightUnregistered from "~/features/salesPartner/components/CompanyDetailsCard/SPCompanyDetailsCardRightUnregistered";
import GmapsCardRightRegistered from "~/features/salesPartner/components/CompanyDetailsCard/SPCompanyDetailsCardRightRegistered";
import { userIsLoggedIn, userIsRegistered } from "~/domains/user/utils/roles";
import { Alert, Button, Stack } from "~/common/mui";
import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import debounce from "lodash/debounce";
import { useShowAffiliateMainBanner } from "~/domains/affiliate/utils/utils";
import { RegisterToSaveSnackbar } from "~/features/salesPartner/components/SalesPartnerSnackbars";
import { trackSalesPartnerFinderSelectDefault } from "~/app/analytics/ga/affiliate";
import { useTranslation } from "react-i18next";
import classnames from "classnames";
import classNames from "classnames";
import { SPCompanyDetailsCard } from "~/features/salesPartner/components/CompanyDetailsCard/SPCompanyDetailsCard";
import { CART_PAGE, CART_STEP_2, REGISTER_PAGE } from "~/utils/url/staticUrls";
import { B2CCartProcessTile } from "~/features/cart/B2CCart/components/B2CCartProcessTile/B2CCartProcessTile";
import classes from "./SalesPartnerPage.module.less";
import { trackPartnerFinderSearch } from "~/app/analytics/ga/salesPartner";
import { transformSelectedMarkerData } from "~/features/cart/B2CRequestPage/utils";
import { selectSavedAffiliate, selectVisitedAffiliate } from "~/domains/affiliate/redux/selectors";
import { clearSavedSubsidiaryThunk, saveAffiliateThunk } from "~/domains/affiliate/redux/actions";
import { usePreferredSalesPartner } from "~/features/salesPartner/hooks/usePreferredSalesPartner";
import { useSalesPartnerSearch } from "~/features/salesPartner/hooks/useSalesPartnerSearch";
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import { useGuestCheckoutDialog } from "~/features/cart/B2CCart/components/GuestCheckoutDialog/useGuestCheckoutDialog";

import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import { Tabs } from "~/common/mui/Tabs/Tabs";
import { Tab } from "~/common/mui/Tabs/Tab";

interface ConfigurationData {
  shareLinearConfigurationData: string[];
  productCompetences: Option[];
  certifiedCompetences: Option[];
  selectedProductCompetences: string[];
}
interface Props {
  data: ConfigurationData | undefined;
}

export const INITIAL_RADIUS = 100;

const RESULTS_ID = "results";

const OFFSET_CONSTANT = 122.88;
const calculateOffset = (zoom: number) => (zoom > 0 ? OFFSET_CONSTANT / 2 ** zoom : OFFSET_CONSTANT);

const SalesPartnerPage = (props: Props) => {
  const isDesktop = useIsMinDesktopSize();
  const isTablet = useIsMinTabletSize();

  const {
    data: { shareLinearConfigurationData, productCompetences, certifiedCompetences, selectedProductCompetences } = {},
  } = props;

  const isCheckout = window.location.pathname.endsWith(CART_STEP_2);

  const PAGE_SIZE = isDesktop || !isCheckout ? 7 : 20;

  const { t } = useTranslation();
  const [zoomValue, setZoomValue] = useState<number>(5);
  const [center, setCenter] = useState<CenterValue>({
    lat: 52.520008,
    lng: 13.404954,
  });
  const [selectedMarker, setSelectedMarker] = useState<GoogleMarker>();
  const [selectedMarkerMap, setSelectedMarkerMap] = useState<GoogleMarker>();
  const [usersCurrentLocation, setUsersCurrentLocation] = useState<CurrentLocation | null>(null);
  const [pageCountryCode, setPageCountryCode] = useState<string>("");
  const [listOfPartnersNames, setListOfPartnersNames] = useState<string[]>([]);
  const [initialized, setInitialized] = useState<boolean>(false);

  const { isLoaded: googleApiLoaded } = useIsGoogleApiLoaded();

  const preferredSalesPartner = usePreferredSalesPartner();
  const savedAffiliate = useSelector(selectSavedAffiliate);
  const visitedAffiliate = useSelector(selectVisitedAffiliate);

  const hasLinearData = !!shareLinearConfigurationData && shareLinearConfigurationData.length > 0;

  const defaultValues: SASearchFormFields = {
    searchType: SearchTypesSP.Address,
    filterByCountry: !preferredSalesPartner,
    filterBySalesPartner: !!preferredSalesPartner,
    radius: preferredSalesPartner ? -1 : INITIAL_RADIUS,
    certifiedCompetence: [],
    productCompetence: isCheckout || hasLinearData ? selectedProductCompetences : [],
    partner: "",
    address: "",
  };

  const formProps = useForm<SASearchFormFields>({
    defaultValues,
  });

  // Use the sales partner search hook for API fetching
  const handleMarkersLoaded = useCallback((markers: GoogleMarker[], selectFirst: boolean) => {
    if (selectFirst) {
      updateSelectedMarker(markers[0]);
    }
  }, []);

  const {
    isLoading: markersLoading,
    allMarkers,
    paginationData,
    error: searchError,
    search: searchSalesPartners,
    searchOnMount,
    setPaginationData,
  } = useSalesPartnerSearch({
    pageSize: PAGE_SIZE,
    preferredSalesPartnerId: preferredSalesPartner?.affiliateId,
    onMarkersLoaded: handleMarkersLoaded,
    getFormValues: formProps.getValues,
  });

  function sortByTranslatedLabel(products: Option[] | undefined) {
    if (!products) {
      return undefined;
    }

    const getEnglishLabel = (label: string) => {
      return t(label, { lng: "en" });
    };

    // Sort by the english translated label
    const productsWithTranslated = products
      .slice()
      .sort((a, b) => getEnglishLabel(a.label).localeCompare(getEnglishLabel(b.label)));

    // Map each item to an object that includes the translated label
    return productsWithTranslated.map((item) => ({
      ...item,
      label: t(item.label),
    }));
  }

  const updateSelectedMarker = useCallback((marker: GoogleMarker | undefined) => {
    setSelectedMarker(marker);
    // For some users the addess in SPMap was not updating correctly, even though the partner name was!
    // I tried using key={...} on SPMap to force updating, but it didn't work.
    // I couldn't reproduce issue locally to work on a proper fix, so the fix for now is:
    //
    // Clear selected marker so SPMap is removed from HTML and then quickly recreate it with correct data
    setSelectedMarkerMap(undefined);
    window.setTimeout(() => setSelectedMarkerMap(marker), 50);
  }, []);

  useEffect(() => {
    if (!googleApiLoaded) {
      return;
    }
    getBasicSalesPartnerData().then((res: SalesPartnerMountRequest) => {
      setPageCountryCode(res.code.toLowerCase());
      fetchLocationHandler(res.code.toLowerCase());
      setListOfPartnersNames(res.salesPartnerNames);
    });
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("wl-id")) {
      dispatch(selectWishlistThunk(String(urlParams.get("wl-id"))));
    }
  }, [googleApiLoaded]);

  const fetchLocationHandler = async (countryCode: string) => {
    let geoEnabled = false;

    const hasPreferredSalesPartner = !!preferredSalesPartner;

    let res: CurrentLocation | null = null;
    try {
      res = await fetchCurrentLocation();
    } catch (e: unknown) {
      if (e instanceof GeolocationPositionError && e.code === GeolocationPositionError.PERMISSION_DENIED) {
        // all okay, just no permission
      } else if (e instanceof GeolocationPositionError && e.code === GeolocationPositionError.POSITION_UNAVAILABLE) {
        // position unavailable or failed internally due to some problem
      } else {
        throw e;
      }
    }

    if (res) {
      geoEnabled = true;
      setUsersCurrentLocation(res);
      setCenter({ lat: res.lat, lng: res.lng });
      formProps.setValue("address", res.fullAddress);

      // Search on mount with geolocation data
      const params: UrlParams = {
        ...formProps.getValues(),
        lat: res.lat,
        lng: res.lng,
        countryCode: hasPreferredSalesPartner ? "" : res.country.isocode,
        radius: hasPreferredSalesPartner ? -1 : INITIAL_RADIUS,
        salesPartnerId: preferredSalesPartner?.affiliateId ?? undefined,
      };
      searchOnMount(params);
    }

    if (!geoEnabled) {
      const country = t(`country.${countryCode}`);
      formProps.setValue("address", country);
      formProps.setValue("radius", -1);

      // Search with country-based params
      const params: UrlParams = {
        ...formProps.getValues(),
        address: country,
        partner: "",
        radius: -1,
        filterByCountry: !hasPreferredSalesPartner,
        searchType: SearchTypesSP.Address,
        countryCode: countryCode,
        salesPartnerId: preferredSalesPartner?.affiliateId ?? undefined,
      };
      searchSalesPartners(params, 0);
    }
    setInitialized(true);
  };

  const { val: isDetailsPopupOpen, setTrue: openDetailsPopup, setFalse: closeDetailsPopup } = useBooleanState(false);

  const selectSalesPartner = (marker: GoogleMarker | undefined, canOpenPopup = true) => {
    updateSelectedMarker(marker);

    const pageIndex = marker ? Math.floor(allMarkers.indexOf(marker) / PAGE_SIZE) : 0;
    if (pageIndex >= 0) {
      setPaginationData((oldData) => ({ ...oldData, currentPage: pageIndex }));
    }

    if (marker) {
      const lat = marker.lat + (isDesktop ? calculateOffset(zoomValue) : 0);
      setCenter({ lat: lat, lng: marker.lng });
      window.location.href = "#" + RESULTS_ID;

      if (!isDesktop && canOpenPopup) {
        openDetailsPopup();
      }
    }
    trackPartnerFinderSearch(
      formProps.getValues(),
      "Sales Partner Selected",
      "Click",
      transformSelectedMarkerData(t, marker),
    );
  };

  const updatePaginationCurrentPage = (newPageNumber: number) => {
    setPaginationData({
      ...paginationData,
      currentPage: newPageNumber,
    });
  };

  const showMoreMarkers = () => {
    setPaginationData({
      ...paginationData,
      pageSize: paginationData.pageSize + PAGE_SIZE,
    });
  };

  const listMarkers = allMarkers.slice(
    paginationData.currentPage * paginationData.pageSize,
    (paginationData.currentPage + 1) * paginationData.pageSize,
  );

  const {
    val: showRegisterSnackbar,
    setTrue: openRegisterSnackbar,
    setFalse: closeRegisterSnackbar,
  } = useBooleanState(false);

  useEffect(() => {
    if (!markersLoading && initialized) {
      const countryCode = usersCurrentLocation ? usersCurrentLocation.country.isocode : pageCountryCode;
      getSalesPartnerTiles(countryCode, preferredSalesPartner?.affiliateId);
    }
  }, []);

  const debounceCallback = debounce((pageCountryCode: string, affiliateId: string | undefined) => {
    salesPartnerFinderRequester({
      ...formProps.getValues(),
      countryCode: pageCountryCode,
      preferredSalesPartner: affiliateId,
    });
  }, 1000);
  const getSalesPartnerTiles = useCallback(debounceCallback, []);

  // Used so the use effect does not trigger on initial render
  const isInitialised = useRef(false);

  useEffect(() => {
    if (!isInitialised.current) {
      return;
    }
    if (savedAffiliate && !userIsLoggedIn()) {
      openRegisterSnackbar();
    }
  }, [savedAffiliate]);

  useEffect(() => {
    isInitialised.current = true;
  }, []);

  useEffect(() => {
    if (preferredSalesPartner) {
      const preferredMarker = allMarkers.find((marker) => marker.affiliateId === preferredSalesPartner.affiliateId);
      updateSelectedMarker(preferredMarker);
    }
  }, [allMarkers]);

  const handleConfirmClick = () => {
    closeDetailsPopup();
    if (selectedMarker?.subsidiaryName) {
      handleB2CCheckout(selectedMarker.subsidiaryId);
    }
  };

  // If a preferred sales partner
  useEffect(() => {
    if (preferredSalesPartner) {
      // reset filterCountry and unlimited radius
      formProps.setValue("filterByCountry", false);
      formProps.setValue("radius", -1);
    } else {
      // back to defaults
      formProps.setValue("filterByCountry", true);
      formProps.setValue("radius", INITIAL_RADIUS);
    }
  }, [preferredSalesPartner]);

  const salesPartnerFinderRequester = (
    data: SASearchFormFields & { countryCode?: string; preferredSalesPartner?: string },
  ) => {
    const objectForUrl: UrlParams = {
      ...data,
      countryCode:
        usersCurrentLocation !== null
          ? usersCurrentLocation.country.isocode
          : pageCountryCode || (data.countryCode ?? ""),
      salesPartnerId: data.filterBySalesPartner ? data.preferredSalesPartner ?? "" : "",
    };
    searchSalesPartners(objectForUrl, 0);
  };

  const submitCb = formProps.handleSubmit((formValues) =>
    salesPartnerFinderRequester({ ...formValues, preferredSalesPartner: preferredSalesPartner?.affiliateId }),
  );

  const setDefaultLocation = (marker: GoogleMarker, isDefault: boolean) => {
    // !userIsPublic() && apicall
    if (isDefault) {
      const address = [marker.address.line1, marker.address.line2, marker.address.town, marker.address.country.name]
        .filter(Boolean)
        .join(" ");
      trackSalesPartnerFinderSelectDefault(marker.address.companyNameDisplay, marker.address.postalCode, address);
      dispatch(
        saveAffiliateThunk({
          name: marker.address.companyNameDisplay,
          affiliateId: marker.affiliateId,
          logo: "",
          subsidiaries: [],
          salesPartnerId: marker.subsidiaryId,
        }),
      );
    } else {
      dispatch(clearSavedSubsidiaryThunk());
    }
  };

  const loggedIn = userIsLoggedIn();
  const registeredUser = userIsRegistered();
  const [activeTab, setActiveTab] = useState("results");

  const results = (
    <SalesPartnersTilesList
      markers={listMarkers}
      selectedMarker={selectedMarker}
      onTileClick={selectSalesPartner}
      paginationData={paginationData}
      markersLoading={markersLoading}
      updatePaginationCurrentPage={updatePaginationCurrentPage}
      showMoreMarkers={showMoreMarkers}
      onToggleDefault={setDefaultLocation}
      isCheckout={isCheckout}
    />
  );

  const map = (
    <SPMapSection
      markers={allMarkers}
      selectedMarker={selectedMarkerMap}
      center={center}
      zoomValue={zoomValue}
      setZoomValue={setZoomValue}
      selectSalesPartner={selectSalesPartner}
      shareLinearConfigurationData={shareLinearConfigurationData}
      openDetailsPopup={openDetailsPopup}
      openRegisterSnackbar={openRegisterSnackbar}
      isCheckout={isCheckout}
      activeTab={activeTab}
    />
  );

  const mounted = useRef(false);

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
  }, [activeTab]);

  const isAffiliate = useShowAffiliateMainBanner();

  const showMapCb = () => {
    setActiveTab("map");
    window.setTimeout(() => redirectToUrl("#" + RESULTS_ID), 0);
    closeDetailsPopup();
  };

  const dispatch = useDispatch();

  const [handleB2CCheckout, guestCheckoutDialog] = useGuestCheckoutDialog();

  const screenSize = useNumericScreenSize();
  const isMobile = screenSize <= useNumericScreenSize.phone;

  return (
    <FormProvider {...formProps}>
      {guestCheckoutDialog}
      {isCheckout && !isAffiliate && <B2CCartProcessTile activeIndex={1} className="mb-4" />}
      <RegisterToSaveSnackbar
        open={showRegisterSnackbar}
        onClose={closeRegisterSnackbar}
        selectedMarker={selectedMarkerMap}
      />
      <form className="scha__sales-partner" onSubmit={submitCb}>
        <h3 className="headline-medium mb-3">{t("partnerFinder.title")}</h3>
        <SearchCard
          usersCurrentLocation={usersCurrentLocation}
          markersLoading={markersLoading}
          pageCountryCode={pageCountryCode}
          listOfPartnersNames={listOfPartnersNames}
          resetForm={() => {
            trackPartnerFinderSearch(formProps.getValues(), "All Information Cleared", "Click");
            formProps.reset({
              ...defaultValues,
              productCompetence: [],
              filterByCountry: false,
              filterBySalesPartner: false,
              radius: -1,
            });
            if (isMobile) {
              submitCb();
            }
          }}
          submitForm={submitCb}
          productCompetencesOptions={sortByTranslatedLabel(productCompetences)}
          certifiedCompetencesOptions={sortByTranslatedLabel(certifiedCompetences)}
          isCheckout={isCheckout || hasLinearData}
        />
      </form>

      {searchError && (
        <Alert severity="error" className="mb-3">
          {t("partnerFinder.error.searchFailed", { defaultValue: searchError.message })}
        </Alert>
      )}

      <div id={RESULTS_ID}>
        {isDesktop && <DesktopLayout results={results} map={map} />}

        {!isDesktop && <MobileLayout results={results} map={map} activeTab={activeTab} setActiveTab={setActiveTab} />}
      </div>

      {isDetailsPopupOpen && selectedMarker && (
        <Modal
          size={ModalSize.SMALL}
          onClose={closeDetailsPopup}
          modalClassName={classnames(!isTablet && "scha__sp__mobile-details")}
          footerClassName={classnames(!isTablet && classes.filterFooter)}
          footerChildren={
            isTablet ? (
              <div className="d-flex flex-justify-between w-100">
                <Button onClick={closeDetailsPopup} variant="text">
                  {t("bulkUpload.buttons.cancel")}
                </Button>
                <div className={classNames("d-flex", classes.buttons, userIsRegistered() && classes.reverse)}>
                  {isCheckout ? (
                    <Button onClick={handleConfirmClick}>{t("partnerFinder.search.confirm")}</Button>
                  ) : (
                    <Button
                      startIcon={<ShoppingCartOutlinedIcon />}
                      color={userIsRegistered() ? "primary" : "secondary"}
                      href={CART_PAGE}
                    >
                      {t("addToCartPopup.checkoutBtn")}
                    </Button>
                  )}
                  {!userIsRegistered() && (
                    <Button href={REGISTER_PAGE} startIcon={<PersonOutlineOutlinedIcon />}>
                      {t("partnerFinder.register.button")}
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <Stack gap={1} direction="row" className="d-flex flex-justify-between w-100">
                {userIsRegistered() ? (
                  <>
                    <Button onClick={closeDetailsPopup} variant="text" fullWidth>
                      {t("checkoutPage.addressModal.cancel")}
                    </Button>
                    {isCheckout ? (
                      <Button onClick={handleConfirmClick} fullWidth>
                        {t("partnerFinder.search.confirm")}
                      </Button>
                    ) : (
                      <Button startIcon={<ShoppingCartOutlinedIcon />} href={CART_PAGE} fullWidth>
                        {t("addToCartPopup.checkoutBtn")}
                      </Button>
                    )}
                  </>
                ) : (
                  <Stack direction="row" gap={1} className="w-100">
                    <Button onClick={closeDetailsPopup} variant="text" fullWidth>
                      {t("bulkUpload.buttons.cancel")}
                    </Button>
                    <Button onClick={handleConfirmClick} fullWidth>
                      {t("partnerFinder.search.confirm")}
                    </Button>
                  </Stack>
                )}
              </Stack>
            )
          }
        >
          {!isTablet ? (
            <>
              <SPCompanyDetails
                company={selectedMarker}
                activeTab={activeTab}
                openRegisterSnackbar={openRegisterSnackbar}
                action={
                  <div className="scha__sp__mobile-details__show-map">
                    <Button color="secondary" startIcon={<Icon type={IconType.Location} />} onClick={showMapCb}>
                      {t("page.salespartner.showMap")}
                    </Button>
                  </div>
                }
              />

              <div>
                {!loggedIn && <GmapsCardRightUnregistered company={selectedMarker} />}
                {registeredUser && !isCheckout && (
                  <GmapsCardRightRegistered
                    shareLinearConfigurationData={shareLinearConfigurationData}
                    companyDetails={selectedMarker.address}
                    close={closeDetailsPopup}
                    company={selectedMarker}
                  />
                )}
              </div>
            </>
          ) : (
            <SPCompanyDetailsCard
              selectedMarker={selectedMarker}
              activeTab={activeTab}
              shareLinearConfigurationData={shareLinearConfigurationData}
              openRegisterSnackbar={openRegisterSnackbar}
              isCheckout={isCheckout}
              className="m-0"
              action={
                isDesktop ? undefined : (
                  <div className="scha__sp__mobile-details__show-map">
                    <Button color="secondary" startIcon={<Icon type={IconType.Location} />} onClick={showMapCb}>
                      {t("page.salespartner.showMap")}
                    </Button>
                  </div>
                )
              }
            />
          )}
        </Modal>
      )}
    </FormProvider>
  );
};

export function createSalesPartnerPage(data?: ConfigurationData) {
  return (
    <GoogleApiProvider>
      <SalesPartnerPage data={data} />
    </GoogleApiProvider>
  );
}

interface LayoutProps {
  results: ReactNode;
  map: ReactNode;
}

interface MobileLayoutProps extends LayoutProps {
  activeTab: string;
  setActiveTab: (a: string) => void;
}

const MobileLayout = ({ results, map, setActiveTab, activeTab }: MobileLayoutProps) => {
  const { t } = useTranslation();

  return (
    <>
      <div className={"scha__sp__tab-container"}>
        <Tabs
          value={activeTab}
          onChange={(e, val) => setActiveTab(val)}
          fullWidthIndicator={false}
          variant={"fullWidth"}
        >
          <Tab
            value={"results"}
            label={
              <span className={"d-flex flex-direction-column flex-align-center"}>
                <DescriptionOutlinedIcon />
                {t("page.salespartner.view.list")}
              </span>
            }
          />
          <Tab
            value={"map"}
            label={
              <span className={"d-flex flex-direction-column flex-align-center"}>
                <FmdGoodOutlinedIcon />
                {t("page.salespartner.view.map")}
              </span>
            }
          />
        </Tabs>
      </div>
      {activeTab === "map" ? map : results}
    </>
  );
};

const DesktopLayout = ({ results, map }: LayoutProps) => {
  return (
    <>
      <Row>
        <Col size={4}>{results}</Col>
        <Col size={8}>{map}</Col>
      </Row>
    </>
  );
};
